import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Container, Row, Col, Button } from "react-bootstrap";

import './App.css';
import './HeroSection.css';
import './PWA.css';

import FilterBar from "./components/FilterBar";
import MovieCard from "./components/MovieCard";
import MovieGridExperiment from "./components/MovieGridExperiment";
import TrailerModal from "./components/TrailerModal";
import MoodQuiz from "./components/MoodQuiz";
import HeroSection from "./components/HeroSection";
import PWAManager from "./components/PWAManager";
import { 
  LoadingSpinner, 
  MovieCardSkeleton, 
  ErrorState, 
  EmptyState, 
  SuccessToast 
} from "./components/LoadingStates";

const logo = `${process.env.PUBLIC_URL}/streaming-mood-logo.svg`;
const logoMobile = `${process.env.PUBLIC_URL}/streaming-mood-logo-mobile.svg`;

const compactMovieKey = (movie) => `${movie.id}-${movie.title || movie.name || ""}`;

// Utility function for debouncing
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const regionOptions = [
  { label: "🇺🇸 United States", value: "US" },
  { label: "🇬🇧 United Kingdom", value: "GB" },
  { label: "🇫🇷 France", value: "FR" },
  { label: "🇪🇸 Spain", value: "ES" },
  { label: "🇯🇵 Japan", value: "JP" },
  { label: "🇩🇪 Germany", value: "DE" },
  { label: "🇹🇷 Turkey", value: "TR" },
  { label: "🇵🇱 Poland", value: "PL" },
  { label: "🇵🇹 Portugal", value: "PT" },
  { label: "🇫🇮 Finland", value: "FI" },
  { label: "🇩🇰 Denmark", value: "DK" },
];

const countryOptions = [
  { label: "🇺🇸 United States", value: "US" },
  { label: "🇫🇷 France", value: "FR" },
  { label: "🇪🇸 Spain", value: "ES" },
  { label: "🇯🇵 Japan", value: "JP" },
  { label: "🇩🇪 Germany", value: "DE" },
  { label: "🇹🇷 Turkey", value: "TR" },
  { label: "🇵🇱 Poland", value: "PL" },
  { label: "🇵🇹 Portugal", value: "PT" },
  { label: "🇫🇮 Finland", value: "FI" },
  { label: "🇩🇰 Denmark", value: "DK" },
];

const ageCategoryOptions = [
  { label: "Adults", value: "Adults" },
  { label: "Young", value: "Young" },
  { label: "Kids", value: "Kids" },
];

const providerOptions = [
  { id: "Any", name: "Any Provider" },
  { id: 8, name: "Netflix" },
  { id: 9, name: "Amazon Prime Video" },
  { id: 337, name: "Disney+" },
  { id: 15, name: "Hulu" },
  { id: 1899, name: "HBO Max" },
  { id: 2, name: "Apple TV" }
];

const App = () => {
  const storedPrefs = JSON.parse(localStorage.getItem("userPreferences") || "{}");
  const [region, setRegion] = useState(storedPrefs.region || 'US');
  const [ageCategory, setAgeCategory] = useState(storedPrefs.ageCategory || 'Adults');
  const [selectedProvider, setSelectedProvider] = useState(storedPrefs.selectedProvider !== undefined ? storedPrefs.selectedProvider : "Any");
  const [sortOption, setSortOption] = useState(storedPrefs.sortOption || "popularity.desc");
  const [videoType, setVideoType] = useState(storedPrefs.videoType || "movie");
  const [quizAnswers, setQuizAnswers] = useState({});
  /* Origin-country filter is not exposed in the UI; keep Any so TMDB discover is not wrongly scoped to US. */
  const [country, setCountry] = useState('Any');
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [currentTrailer, setCurrentTrailer] = useState('');
  const [expandedMovieKey, setExpandedMovieKey] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showHero, setShowHero] = useState(() => {
    // Check if user has seen hero before
    const hasSeenHero = localStorage.getItem('hasSeenHero');
    const shouldShowHero = !hasSeenHero;
    
    console.log('🎬 Hero page caching check:', {
      hasSeenBefore: !!hasSeenHero,
      willShowHero: shouldShowHero,
      isFirstTimeUser: !hasSeenHero
    });
    
    return shouldShowHero; // Show hero only if user hasn't seen it before
  });

  useEffect(() => {
    const saved = localStorage.getItem("userPreferences");
    if (saved) {
      const prefs = JSON.parse(saved);
      if (prefs.region) setRegion(prefs.region);
      if (prefs.selectedProvider) setSelectedProvider(prefs.selectedProvider);
      if (prefs.ageCategory) setAgeCategory(prefs.ageCategory);
      if (prefs.sortOption) setSortOption(prefs.sortOption);
      if (prefs.videoType) setVideoType(prefs.videoType);
    }
  }, []);
  
  useEffect(() => {
    localStorage.setItem("userPreferences", JSON.stringify({
      region,
      selectedProvider,
      ageCategory,
      sortOption,
      videoType
    }));
  }, [region, selectedProvider, ageCategory, sortOption, videoType]);

  // Debounced function to prevent rapid API calls
  const debouncedQuizComplete = useCallback(
    debounce(async (answers, requestData) => {
      try {
         /*const response = await axios.post('http://localhost:5000/recommend', requestData);*/
         const response = await axios.post('/recommend', requestData); /*This should be always active in the github to use application live in Render*/
        
        if (response.data && Array.isArray(response.data.movies)) {
          setMovies(response.data.movies);
          setHasMore(response.data.has_more || false); // Use backend pagination info
          setError(null); // Clear any previous errors
          
          // Show success message
          if (response.data.movies.length > 0) {
            setShowSuccessToast(true);
            setTimeout(() => setShowSuccessToast(false), 3000);
          }
        } else {
          setMovies([]);
          setHasMore(false);
          // Handle empty results
          if (response.data && response.data.message) {
            setError({
              type: 'info',
              title: 'No recommendations found',
              message: response.data.message
            });
          }
        }
        
      } catch (error) {
        console.error("Error fetching recommendations:", error);
        setMovies([]);
        setHasMore(false);
        
        // Enhanced error handling
        let errorMessage = "We're having trouble connecting to our recommendation service.";
        if (error.response) {
          if (error.response.status === 503) {
            errorMessage = "Our movie database is temporarily unavailable. Please try again in a few moments.";
          } else if (error.response.status === 400) {
            errorMessage = "There seems to be an issue with your request. Please try different selections.";
          } else if (error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        } else if (error.request) {
          errorMessage = "Unable to connect to the server. Please check your internet connection.";
        }
        
        setError({
          type: 'error',
          title: 'Unable to fetch recommendations',
          message: errorMessage
        });
      }
      setLoading(false);
    }, 300),
    [selectedProvider, sortOption, videoType, region, country, ageCategory]
  );

  // Function to refresh recommendations with current mood and updated filters
  const refreshRecommendations = useCallback((newFilters = {}) => {
    if (quizCompleted && quizAnswers && Object.keys(quizAnswers).length > 0 && movies.length > 0 && !loading) {
      console.log("Refreshing recommendations with updated filters...", newFilters);
      
      setMovies([]);
      setLoading(true);
      setPage(1);
      setHasMore(false);
      setError(null);

      const requestData = {
        quizAnswers,
        sort: newFilters.sort || sortOption,
        video: newFilters.video || videoType,
        region: newFilters.region || region,
        country: newFilters.country || country,
        ageCategory: newFilters.ageCategory || ageCategory,
        providers: newFilters.providers || selectedProvider,
        page: 1
      };

      debouncedQuizComplete(quizAnswers, requestData);
    }
  }, [quizCompleted, quizAnswers, movies.length, loading, sortOption, videoType, region, country, ageCategory, selectedProvider, debouncedQuizComplete]);

  // Enhanced setters that trigger refresh when filters change
  const setSortOptionWithRefresh = useCallback((value) => {
    setSortOption(value);
    setTimeout(() => refreshRecommendations({ sort: value }), 150);
  }, [refreshRecommendations]);

  const setVideoTypeWithRefresh = useCallback((value) => {
    setVideoType(value);
    setTimeout(() => refreshRecommendations({ video: value }), 150);
  }, [refreshRecommendations]);

  const setRegionWithRefresh = useCallback((value) => {
    setRegion(value);
    setTimeout(() => refreshRecommendations({ region: value }), 150);
  }, [refreshRecommendations]);

  const setSelectedProviderWithRefresh = useCallback((value) => {
    setSelectedProvider(value);
    setTimeout(() => refreshRecommendations({ providers: value }), 150);
  }, [refreshRecommendations]);

  const setAgeCategoryWithRefresh = useCallback((value) => {
    setAgeCategory(value);
    setTimeout(() => refreshRecommendations({ ageCategory: value }), 150);
  }, [refreshRecommendations]);

  const setCountryWithRefresh = useCallback((value) => {
    setCountry(value);
    setTimeout(() => refreshRecommendations({ country: value }), 150);
  }, [refreshRecommendations]);

  const handleQuizComplete = async (answers) => {
    setMovies([]); // Always clear existing movies
    setExpandedMovieKey(null);
    setError(null); // Clear any previous errors
    
    // If this is just a clear request (mood change), don't fetch new recommendations
    if (answers.clearOnly) {
      setQuizCompleted(false);
      setLoading(false);
      return;
    }
    
    setQuizAnswers(answers);
    setQuizCompleted(true);
    setLoading(true);
    setPage(1);
    setHasMore(false);

    const requestData = {
      quizAnswers: answers,
      sort: sortOption,
      video: videoType,
      region,
      country,
      ageCategory,
      providers: selectedProvider,
      page: 1
    };

    debouncedQuizComplete(answers, requestData);
  };

  // Function to retry fetching recommendations
  const handleRetry = () => {
    if (quizAnswers && Object.keys(quizAnswers).length > 0) {
      handleQuizComplete(quizAnswers);
    }
  };


  // Function to reset the application state
  const handleReset = () => {
    setMovies([]);
    setExpandedMovieKey(null);
    setQuizAnswers({});
    setQuizCompleted(false);
    setError(null);
    setPage(1);
    setHasMore(false);
    setShowHero(true);
  };

  // Function to start quiz from hero section
  const handleStartQuiz = () => {
    // Mark that user has seen the hero page
    localStorage.setItem('hasSeenHero', 'true');
    console.log('Hero page cached - user will skip hero on future visits');
    setShowHero(false);
  };

  const loadMore = async () => {
    const nextPage = page + 1;
    setLoading(true);

    try {
      const requestData = {
        quizAnswers,
        sort: sortOption,
        video: videoType,
        region,
        country,
        ageCategory,
        providers: selectedProvider,
        page: nextPage
      };

      /*const response = await axios.post('http://localhost:5000/recommend', requestData);*/
      const response = await axios.post('/recommend', requestData); /*This should be always active in the github to use application live in Render*/
      setMovies((prev) => [...prev, ...response.data.movies]);
      setPage(nextPage);
      setHasMore(response.data.has_more || false); // Use backend pagination info
    } catch (error) {
      console.error("Error loading more recommendations:", error);
    }

    setLoading(false);
  };

  const handleShowTrailer = (url) => {
    setCurrentTrailer(url);
    setShowTrailer(true);
  };

  const handleCloseTrailer = () => {
    setCurrentTrailer('');
    setShowTrailer(false);
  };

  return (
    <>
      {/* Hero Section */}
      {showHero && (
        <HeroSection onStartQuiz={handleStartQuiz} />
      )}

      {/* Main App Content */}
      {!showHero && (
        <div className="main-app-wrapper">
          <Container className="pt-1 pb-4 main-content-container">
          <div className="cinematic-panel-compact main-header-panel mb-2">
            <div className="panel-content-compact main-header-row d-flex flex-row flex-wrap align-items-center justify-content-center gap-2">
              <div className="d-flex align-items-center flex-shrink-0">
                <img
                  src={logo}
                  alt="StreamingMood Logo"
                  className="logo-desktop main-header-logo"
                  width={36}
                  height={36}
                  style={{ height: "36px", width: "36px" }}
                />
                <img
                  src={logoMobile}
                  alt="StreamingMood Logo"
                  className="logo-mobile main-header-logo"
                  width={36}
                  height={36}
                  style={{ height: "36px", width: "36px" }}
                />
              </div>
              <div className="text-center">
                <h2 className="mb-0 main-header-title">StreamingMood</h2>
                <p className="main-header-tagline mb-0">Mood-based streaming picks</p>
              </div>
            </div>
          </div>

          <FilterBar
            region={region}
            setRegion={setRegionWithRefresh}
            country={country}
            setCountry={setCountryWithRefresh}
            ageCategory={ageCategory}
            setAgeCategory={setAgeCategoryWithRefresh}
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProviderWithRefresh}
            regionOptions={regionOptions}
            countryOptions={countryOptions}
            ageCategoryOptions={ageCategoryOptions}
            providerOptions={providerOptions}
            sortOption={sortOption}
            setSortOption={setSortOptionWithRefresh}
            videoType={videoType}
            setVideoType={setVideoTypeWithRefresh}
          />

          <MoodQuiz 
            onComplete={handleQuizComplete} 
            hasRecommendations={movies.length > 0}
            isLoading={loading}
            quizCompleted={quizCompleted}
          />

      {/* Loading State */}
      {loading && !movies.length && (
        <LoadingSpinner 
          message={quizCompleted ? "Analyzing your mood and finding perfect matches..." : "Loading recommendations..."} 
        />
      )}

      {/* Error State */}
      {error && !loading && (
        <ErrorState
          title={error.title}
          message={error.message}
          type={error.type}
          onRetry={error.type === 'error' ? handleRetry : null}
        />
      )}

      {/* Empty State - No results after quiz completion */}
      {!loading && !error && quizCompleted && movies.length === 0 && (
        <EmptyState
          title="No movies match your current mood"
          message="Don't worry! Try adjusting your preferences or take the quiz again with different answers."
          actionText="🎭 Retake Quiz"
          onAction={handleReset}
        />
      )}

      {/* Results */}
      {movies.length > 0 && (
        <>
          <div className="mb-4 mt-5">
            <h3 className="mb-0 text-center">
              <span role="img" aria-label="movie camera">🎥</span> Your Personalized Recommendations
            </h3>
          </div>
          
          {/* lg+: same grid as before (desktop / large browser window) */}
          <div className="d-none d-lg-block">
            <Row className="mt-3">
              {movies.map((movie) => (
                <Col xs={12} sm={6} md={4} lg={3} className="mb-4" key={`${movie.id}-${movie.title}`}>
                  <MovieCard
                    movie={movie}
                    handleShowTrailer={handleShowTrailer}
                    selectedProvider={selectedProvider}
                  />
                </Col>
              ))}
            </Row>

            {loading && movies.length > 0 && (
              <Row className="mt-3">
                {[...Array(4)].map((_, index) => (
                  <Col xs={12} sm={6} md={4} lg={3} className="mb-4" key={`skeleton-${index}`}>
                    <MovieCardSkeleton />
                  </Col>
                ))}
              </Row>
            )}
          </div>

          {/* Below lg: experiment — 2-col 16:9 tiles; tap expands full MovieCard below grid */}
          <div className="d-lg-none movie-grid-experiment-outer mt-3">
            <MovieGridExperiment
              movies={movies}
              expandedMovieKey={expandedMovieKey}
              movieKey={compactMovieKey}
              onToggle={(m) => {
                const k = compactMovieKey(m);
                setExpandedMovieKey((prev) => (prev === k ? null : k));
              }}
              handleShowTrailer={handleShowTrailer}
              selectedProvider={selectedProvider}
            />
            {loading && movies.length > 0 && (
              <p className="text-center text-muted small py-3 mb-0">Loading more…</p>
            )}
          </div>
        </>
      )}

      {/* Load More Button */}
      {movies.length > 0 && hasMore && !loading && (
        <div className="text-center mb-4">
          <Button 
            onClick={loadMore} 
            variant="primary" 
            size="lg"
            disabled={loading}
            style={{ borderRadius: '25px', padding: '10px 30px' }}
          >
            <span role="img" aria-label="film">🎬</span> Show More {videoType === 'tv' ? 'Series' : 'Movies'}
          </Button>
        </div>
      )}

          <TrailerModal
            show={showTrailer}
            trailerUrl={currentTrailer}
            handleClose={handleCloseTrailer}
          />
        </Container>
        </div>
      )}

      {/* Success Toast - Always available */}
      <SuccessToast
        show={showSuccessToast}
        message={`🎉 Found ${movies.length} perfect recommendations for your mood!`}
        onClose={() => setShowSuccessToast(false)}
      />

      {/* PWA Manager - Mobile app functionality */}
      <PWAManager />
    </>
  );
};

export default App;
